import React, { useState, useEffect, useMemo, useRef, memo } from 'react';
import { useImmer } from 'use-immer';

import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Checkbox, Paper } from '@mui/material';
import formatCurrency, { toOre } from './Formatting';
import { addToCallChain, reportError } from './CallCenter';
import { Loading, LoadError } from './useApiData';
import { isValidCostCenterItem } from './CostCenterItemSelect';
import EventorEventSelect from './EventorEventSelect';
import { useScopeKeys, scopedTree, noIncomePairReason } from './Scope';
import { Account, CostCenterItem, EventInfo, EventorInvoiceImportData, InvoiceInfo, InvoiceSplit, ListEventData, ProjectWithIncomeAccounts } from './Types';
import { IncomePlace, SplitTable, ThreeSelector, isCompletePlace } from './EventorInvoiceSplit';
import { apiGetEventorInvoiceImportData, apiGetEventorEventInvoices, apiResplitEventorInvoices } from './Api';
import { CourierBand, CourierWaiting, useCourier } from './Courier';


// Importable = Eventor has sent it, it isn't already booked in Visma, and it is for something. The
// rest are shown but locked (wrong Eventor status, already imported, or 0 kr — Eventor leaves an
// emptied invoice behind at 0 kr, which books nothing).
const isZero = (invoice: InvoiceInfo) => invoice.total_amount === 0
const isImportable = (invoice: InvoiceInfo) => invoice.status === 'Faktura skickad' && !invoice.already_imported && !isZero(invoice)
const refOf = (invoice: InvoiceInfo) => String(invoice.invoice_number)

// Per-row outcome of an import in progress; 'pending' renders a spinner.
const RESULT: Record<string, { label: string, color: string }> = {
    imported_now:     { label: 'Importerad',     color: '#2e7d32' },
    already_imported: { label: 'Fanns redan',    color: '#666' },
    skipped:          { label: 'Hoppades över',  color: '#666' },
    import_failed:    { label: 'Misslyckades',   color: '#c62828' },
    // Short, for the narrow column; the warning above the list says why and what to do.
    held_unsplit:     { label: 'Ej importerad',  color: '#b26a00' },
    held_unseen:      { label: 'Ej importerad',  color: '#b26a00' },
}
const REASON: Record<string, string> = {
    held_unsplit: 'Rader i fakturan kunde inte delas upp.',
    held_unseen: 'Fakturan har rader som förhandsgranskningen inte visade.',
}
// An invoice with one of these outcomes is still not in Visma, and can be selected and imported
// again: after a failure, or once lines that could not be split have a rule or a place.
const RETRYABLE = new Set(['import_failed', 'held_unsplit', 'held_unseen'])
// And one with these is in Visma now, though the list still says otherwise until it is reloaded.
const BOOKED = new Set(['imported_now', 'already_imported'])

const Row = ({ invoice, checked, onToggle, disabled, result }: {
    invoice: InvoiceInfo, checked: boolean, onToggle: () => void, disabled: boolean, result: string | undefined,
}) => {
    const importable = isImportable(invoice)
    const outcome = result !== undefined &&
        <span style={{ color: RESULT[result]?.color ?? '#666', fontSize: '0.85em' }}
              title={REASON[result]}>
            {RESULT[result]?.label ?? result}
        </span>
    let first: React.ReactNode = null
    if (result === 'pending') first = <CircularProgress size="1em"/>
    else if (result !== undefined && RETRYABLE.has(result) && importable)
        first = <div style={{ display: 'flex', flexFlow: 'column', alignItems: 'center' }}>
            <Checkbox size="small" checked={checked} disabled={disabled} onChange={onToggle}/>{outcome}
        </div>
    else if (result !== undefined) first = outcome
    else if (invoice.already_imported) first = invoice.visma_paid
        ? <span style={{ color: '#2e7d32', fontSize: '0.85em' }}>Betald</span>
        : <span style={{ color: '#1565c0', fontSize: '0.85em' }}>Importerad</span>
    else if (importable) first = <Checkbox size="small" checked={checked} disabled={disabled} onChange={onToggle}/>
    else if (isZero(invoice)) first = <span style={{ color: '#666', fontSize: '0.85em' }} title="0 kr – importeras inte">Nollfaktura</span>
    return (
        <TableRow hover sx={importable || invoice.already_imported ? undefined : { '& td': { color: 'text.disabled' } }}>
            <TableCell padding="checkbox" align="center">{first}</TableCell>
            <TableCell>{invoice.org_name}</TableCell>
            <TableCell>{invoice.invoice_number}</TableCell>
            <TableCell>{invoice.status}</TableCell>
            <TableCell align="right">{formatCurrency(invoice.total_amount)}</TableCell>
        </TableRow>
    )
}

const EventorInvoiceImport = memo(({ onUpdate }: { onUpdate: () => void }) => {
    // Static lookups (events + the cost-center/project/account tree), loaded once.
    const [events, setEvents] = useState<EventInfo[] | null>(null)
    const [costCenterItems, setCostCenterItems] = useState<CostCenterItem<ProjectWithIncomeAccounts>[] | null>(null)
    const [officialIncomeAccounts, setOfficialIncomeAccounts] = useState<Account[]>([])
    useEffect(() => {
        apiGetEventorInvoiceImportData().then((data: EventorInvoiceImportData) => {
            setEvents(data.events)
            setCostCenterItems(data.cost_center_items)
            setOfficialIncomeAccounts(data.official_income_accounts)
        })
    }, [])

    const [eventId, setEventId] = useState<number | null>(null)
    const [costCenterItemId, setCostCenterItemId] = useState<string | null>(null)
    const [projectId, setProjectId] = useState<string | null>(null)
    const [incomeAccount, setIncomeAccount] = useState<number | null>(null)
    // This import books income to Visma, which the backend hard-scopes (no "Visa alla" bypass), so
    // the picker must offer only the active role's pairs — otherwise it would offer locations the
    // import then 403s on. Same client-side scope filter the income invoice rows use.
    const scopeKeys = useScopeKeys()

    // Step 1: the read-only invoice list for the chosen event. Fetched on demand rather than when
    // the event changes: reading Eventor now costs the user a click in another tab (Courier.tsx),
    // and the fetch has to happen *inside* the courier job — a job ends when its work returns, so
    // starting one and letting an effect do the fetching afterwards leaves the request waiting on a
    // browser that has already stopped.
    const courier = useCourier()
    const [listData, setListData] = useImmer<ListEventData | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<unknown>(null)
    const invoices = listData?.invoices ?? null

    const reload = () => {
        if (eventId === null) return
        setLoading(true)
        setError(null)
        void courier.run(async () => {
            try {
                setListData(await apiGetEventorEventInvoices(eventId))
            } catch (e: unknown) {
                setError(e)
            } finally {
                setLoading(false)
            }
        })
    }

    const [selected, setSelected] = useState<Set<string>>(new Set())
    const [busy, setBusy] = useState(false)
    const [results, setResults] = useState<Record<string, string>>({})

    // A new event (or a reload) clears the selection and any per-row results.
    useEffect(() => { setSelected(new Set()); setResults({}) }, [eventId, listData])

    // Splitting the invoices over several places (optional). Each part books "som ovan" until it is
    // given a place of its own. The ordinary and late fee are known without reading anything; the
    // other kinds of line (rental cards, …) only show on the invoices, so ticking the box starts
    // reading them — and the reading can be stopped once the list of parts looks complete, since the
    // import takes every invoice apart again anyway. A new event or a reloaded list starts over.
    const [splitOn, setSplitOn] = useState(false)
    const [splits, setSplits] = useState<Record<string, InvoiceSplit>>({})
    const [places, setPlaces] = useState<Record<string, IncomePlace>>({})
    const [reading, setReading] = useState<{ done: number, total: number } | null>(null)
    const stopReading = useRef<(() => void) | null>(null)
    // Whether Eventor has a late fee for the event at all. When it has none, a race-day price set up
    // only in OLA (270 kr where Eventor says 180) cannot be taken apart, so the treasurer may state
    // the surcharge — applied to what has been read, and to what is read and imported after.
    const [hasLateFee, setHasLateFee] = useState<boolean | null>(null)
    const [surchargeText, setSurchargeText] = useState('')
    const [lateSurcharge, setLateSurcharge] = useState<number | null>(null)
    useEffect(() => {
        stopReading.current?.()
        setSplitOn(false); setSplits({}); setPlaces({})
        setHasLateFee(null); setSurchargeText(''); setLateSurcharge(null)
    }, [eventId, listData])

    // What can be selected: importable by the list, and not booked since it was loaded.
    const importable = useMemo(() => (invoices ?? []).filter(
        invoice => isImportable(invoice) && !BOOKED.has(results[refOf(invoice)] ?? '')), [invoices, results])
    const selectedRefs = importable.filter(invoice => selected.has(refOf(invoice))).map(refOf)
    const heldCount = Object.values(results).filter(result => result === 'held_unsplit').length
    const unseenCount = Object.values(results).filter(result => result === 'held_unseen').length
    const allSelected = importable.length > 0 && importable.every(invoice => selected.has(refOf(invoice)))

    const toggle = (ref: string) => setSelected(s => { const n = new Set(s); n.has(ref) ? n.delete(ref) : n.add(ref); return n })
    const toggleAll = () => setSelected(allSelected ? new Set() : new Set(importable.map(refOf)))

    const locationValid = costCenterItems !== null && costCenterItemId !== null &&
        isValidCostCenterItem({ costCenterItems, costCenterItemId }) && projectId !== null && incomeAccount !== null

    const readSplits = (refs: string[]) => {
        if (eventId === null || refs.length === 0) return
        setReading({ done: 0, total: refs.length })
        const url = new URL('../incomes/eventor/invoice-splits', document.baseURI)
        url.searchParams.append('event_id', String(eventId))
        url.searchParams.append('refs', refs.join(','))
        if (lateSurcharge !== null) url.searchParams.append('late_surcharge_percent', String(lateSurcharge))
        void courier.run(() => new Promise<void>(done => {
        const source = new EventSource(url)
        const stop = () => { source.close(); setReading(null); stopReading.current = null; done() }
        stopReading.current = stop
        source.addEventListener('eventFees', (e: MessageEvent<string>) => setHasLateFee(JSON.parse(e.data).has_late_fee))
        source.addEventListener('invoiceSplit', (e: MessageEvent<string>) => {
            const split = JSON.parse(e.data) as InvoiceSplit
            setSplits(s => ({ ...s, [String(split.invoice_number)]: split }))
            setReading(r => r === null ? r : { ...r, done: r.done + 1 })
        })
        // Eventor did not answer for this one, even when asked again. It stays unread, and is offered
        // for reading again below, rather than showing up as something that could not be split.
        source.addEventListener('invoiceUnread', () => setReading(r => r === null ? r : { ...r, done: r.done + 1 }))
        source.addEventListener('finished', stop)
        source.addEventListener('importError', (e: MessageEvent<string>) => {
            stop()
            reportError({ status: 502, code: 'import_failed', message: JSON.parse(e.data).message })
        })
        source.onerror = () => {
            stop()
            reportError({ status: 0, code: 'import_connection_lost', message: 'Anslutningen bröts medan fakturaraderna lästes.' })
        }
        }))
    }
    const unread = importable.map(refOf).filter(ref => splits[ref] === undefined)
    // The surcharge as typed, if it is one: a percentage above 0 and at most 200, or nothing at all.
    const typedSurcharge = surchargeText.trim() === '' ? null : Number(surchargeText.trim().replace(',', '.'))
    const surchargeValid = typedSurcharge === null || (Number.isFinite(typedSurcharge) && typedSurcharge > 0 && typedSurcharge <= 200)
    const applySurcharge = () => {
        if (eventId === null || !surchargeValid) return
        const read = Object.values(splits)
        addToCallChain(async () => {
            const { splits: again } = await apiResplitEventorInvoices({
                event_id: eventId, late_surcharge_percent: typedSurcharge,
                invoices: read.map(split => ({
                    invoice_number: split.invoice_number,
                    total_amount: invoices?.find(invoice => invoice.invoice_number === split.invoice_number)?.total_amount ?? 0,
                    lines: split.read_lines })) })
            setSplits(s => ({ ...s, ...Object.fromEntries(again.map(split => [String(split.invoice_number), split])) }))
            setLateSurcharge(typedSurcharge)
        }, true)
    }

    const toggleSplit = () => {
        if (splitOn) { stopReading.current?.(); setSplitOn(false); setPlaces({}); return }
        setSplitOn(true)
        readSplits(unread)
    }

    // Only what was moved somewhere else travels with the import; the rest books on the place above.
    const activePlaces = splitOn ? places : {}
    const placesValid = Object.values(activePlaces).every(place => isCompletePlace(place) && costCenterItems !== null &&
        isValidCostCenterItem({ costCenterItems, costCenterItemId: place.costCenterItemId }))

    const importSelected = () => {
        if (!locationValid || !placesValid || selectedRefs.length === 0) return
        setBusy(true)
        // Added to, not replaced: an earlier import's outcomes still say what is in Visma until the list is reloaded.
        setResults(r => ({ ...r, ...Object.fromEntries(selectedRefs.map(ref => [ref, 'pending'])) }))
        setSelected(new Set())

        const url = new URL('../incomes/eventor/importinvoices', document.baseURI)
        url.searchParams.append('event_id', String(eventId))
        url.searchParams.append('cost_center_item_id', costCenterItemId as string)
        url.searchParams.append('project_id', projectId as string)
        url.searchParams.append('income_account_number', String(incomeAccount))
        url.searchParams.append('refs', selectedRefs.join(','))
        if (Object.keys(activePlaces).length > 0) {
            url.searchParams.append('places', JSON.stringify(Object.fromEntries(Object.entries(activePlaces).map(
                ([part, place]) => [part, { cost_center_item_id: place.costCenterItemId, project_id: place.projectId,
                                            income_account_number: place.incomeAccount }]))))
            if (lateSurcharge !== null) url.searchParams.append('late_surcharge_percent', String(lateSurcharge))
            // What the table showed: an invoice with any other kind of line is held rather than booked
            // "som ovan" unseen — it may hold a kind of line only unread invoices have.
            const shown = new Set(['ordinary', 'late'])
            Object.values(splits).forEach(split => split.parts.forEach(part => shown.add(part.key)))
            url.searchParams.append('seen_parts', JSON.stringify([...shown]))
            // And every entry amount it read with how it was split, in öre: a line split any other way
            // is held, since nobody has seen that the rules took it apart rightly.
            const splitsShown = new Map<string, number[]>()
            Object.values(splits).forEach(split => split.entry_amounts.filter(row => !row.unsplit).forEach(row => {
                const ore = [toOre(row.amount), toOre(row.ordinary), toOre(row.late)]
                splitsShown.set(ore.join('|'), ore)
            }))
            url.searchParams.append('seen_entry_splits', JSON.stringify([...splitsShown.values()]))
        }

        // The job must outlive the whole stream: the server fetches from Eventor for every
        // invoice as it goes, so it is a promise that settles when the stream ends.
        void courier.run(() => new Promise<void>(done => {
        const source = new EventSource(url)
        // We already show the list; ignore 'invoicesListed' and just track per-row status.
        source.addEventListener('updateImportStatus', (e: MessageEvent<string>) => {
            const d = JSON.parse(e.data)
            setResults(r => ({ ...r, [String(d.invoice_number)]: d.import_status }))
        })
        source.addEventListener('finished', () => { source.close(); setBusy(false); onUpdate(); done() })
        source.addEventListener('importError', (e: MessageEvent<string>) => {
            source.close(); setBusy(false); done()
            reportError({ status: 502, code: 'import_failed', message: JSON.parse(e.data).message })
        })
        source.onerror = () => {
            source.close(); setBusy(false); done()
            reportError({ status: 0, code: 'import_connection_lost', message: 'Anslutningen till importen bröts.' })
        }
        }))
    }

    if (events === null || costCenterItems === null) return 'Laddar…'

    // Pick from the in-scope pairs only; account resolution still uses the picked (scoped) project.
    const scopedItems = scopedTree(costCenterItems, scopeKeys, costCenterItemId, projectId)
    const noPairReason = noIncomePairReason(costCenterItems, scopeKeys)

    return (
        <div style={{ display: 'flex', flexFlow: 'column nowrap', alignItems: 'flex-start', gap: '0.5rem' }}>
            <div style={{ width: '100%', maxWidth: '60rem' }}>
                <CourierBand/>
                <CourierWaiting waiting={courier.needsClick}/>
            </div>
            <EventorEventSelect required={true} events={events} value={eventId} onChange={setEventId}/>
            {noPairReason !== null &&
                <Alert severity="info" sx={{ alignSelf: 'stretch' }}>{noPairReason}</Alert>}
            <div style={{ display: 'flex', flexFlow: 'row wrap', gap: '1.5rem', alignItems: 'center' }}>
                <ThreeSelector
                    costCenterItems={scopedItems} officialIncomeAccounts={officialIncomeAccounts}
                    costCenterItemId={costCenterItemId} projectId={projectId} incomeAccount={incomeAccount}
                    setCostCenterItemId={setCostCenterItemId} setProjectId={setProjectId} setIncomeAccount={setIncomeAccount}/>
            </div>

            {eventId !== null &&
                <div style={{ display: 'flex', flexFlow: 'row wrap', gap: '0.5rem', alignItems: 'center' }}>
                    <Button variant="contained" disabled={loading || busy || !locationValid || !placesValid || selectedRefs.length === 0} onClick={importSelected}>
                        Importera valda → Visma ({selectedRefs.length})
                    </Button>
                    <Button variant="outlined" disabled={loading || busy} onClick={() => reload()}>Uppdatera listan</Button>
                    {!locationValid && invoices !== null && invoices.length > 0 &&
                        <span style={{ color: '#666', fontSize: '0.85em' }}>Välj kostnadsställe, projekt och konto för att importera.</span>}
                    {locationValid && !placesValid &&
                        <span style={{ color: '#666', fontSize: '0.85em' }}>Välj kostnadsställe, projekt och konto för varje del du flyttat.</span>}
                    {loading && invoices !== null && <CircularProgress size="1.2em"/>}
                </div>
            }

            {!busy && heldCount > 0 &&
                <Alert severity="warning" sx={{ alignSelf: 'stretch', maxWidth: '60rem' }}>
                    {heldCount === 1 ? '1 faktura importerades inte' : `${heldCount} fakturor importerades inte`}, eftersom
                    rader i {heldCount === 1 ? 'den' : 'dem'} inte kunde delas upp. Se raderna under ”Kunde inte delas upp”
                    (läs in {heldCount === 1 ? 'fakturan' : 'fakturorna'} först om det behövs), lägg till ett tillägg eller ge
                    raden en plats med Ändra, och importera {heldCount === 1 ? 'den' : 'dem'} igen.
                </Alert>}
            {!busy && unseenCount > 0 &&
                <Alert severity="warning" sx={{ alignSelf: 'stretch', maxWidth: '60rem' }}>
                    {unseenCount === 1 ? '1 faktura importerades inte' : `${unseenCount} fakturor importerades inte`}, eftersom
                    {unseenCount === 1 ? ' den har' : ' de har'} rader som förhandsgranskningen inte visade — ett nytt slags
                    rad, eller ett belopp som inte fanns bland de inlästa. Läs in {unseenCount === 1 ? 'den' : 'dem'}, kontrollera
                    raderna i tabellen och per belopp, och importera {unseenCount === 1 ? 'den' : 'dem'} igen.
                </Alert>}
            {invoices !== null && importable.length > 0 &&
                <div style={{ display: 'flex', flexFlow: 'row wrap', gap: '1rem', alignItems: 'center' }}>
                    <FormControlLabel
                        sx={{ ml: 0, mr: 0 }}
                        control={<Checkbox size="small" checked={splitOn} disabled={busy || loading} onChange={toggleSplit}/>}
                        label={<Typography variant="body2">Dela upp på flera platser</Typography>}/>
                    {reading !== null && <>
                        <span style={{ color: '#666', fontSize: '0.85em', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <CircularProgress size="1em"/> Läser fakturaraderna … {reading.done} / {reading.total}
                        </span>
                        <Button size="small" onClick={() => stopReading.current?.()}>Sluta läsa</Button>
                    </>}
                    {splitOn && reading === null && unread.length > 0 && <>
                        <span style={{ color: '#666', fontSize: '0.85em' }}>
                            {unread.length === 1 ? '1 faktura är inte inläst.' : `${unread.length} fakturor är inte inlästa.`}
                        </span>
                        <Button size="small" onClick={() => readSplits(unread)}>Läs in {unread.length === 1 ? 'den' : 'dem'}</Button>
                    </>}
                </div>}
            {splitOn && hasLateFee === false &&
                <div style={{ display: 'flex', flexFlow: 'row wrap', gap: '0.75rem', alignItems: 'center', maxWidth: '60rem' }}>
                    <span style={{ fontSize: '0.85em', color: '#444' }}>
                        Eventor har ingen efteranmälningsavgift för tävlingen. Kostade en anmälan mer på tävlingsdagen,
                        ange tillägget så delas sådana rader upp:
                    </span>
                    <TextField size="small" id="late-surcharge" label="Tillägg (%)" value={surchargeText}
                        onChange={e => setSurchargeText(e.target.value)} error={!surchargeValid}
                        sx={{ width: '8rem' }} inputProps={{ inputMode: 'decimal' }}/>
                    <Button size="small" variant="outlined" disabled={!surchargeValid || typedSurcharge === lateSurcharge || reading !== null}
                        onClick={applySurcharge}>Använd</Button>
                    {lateSurcharge !== null &&
                        <span style={{ fontSize: '0.85em', color: '#2e7d32' }}>{lateSurcharge} % används.</span>}
                </div>}
            {splitOn && invoices !== null &&
                <SplitTable
                    invoices={invoices} splits={splits} selectedRefs={selectedRefs}
                    defaultPlace={{ costCenterItemId, projectId, incomeAccount }}
                    places={places} setPlaces={setPlaces}
                    costCenterItems={costCenterItems} officialIncomeAccounts={officialIncomeAccounts} scopeKeys={scopeKeys}/>}

            {eventId !== null && loading && invoices === null && <Loading/>}
            {error !== null && !loading && <LoadError onRetry={() => reload()}/>}
            {invoices !== null && invoices.length === 0 && !loading && <div>Inga fakturor för det här arrangemanget.</div>}
            {invoices !== null && invoices.length > 0 &&
                <TableContainer component={Paper} variant="outlined" sx={{ width: 'fit-content', maxWidth: '100%' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox" align="center">
                                    <Checkbox size="small" checked={allSelected} disabled={busy || loading || importable.length === 0} onChange={toggleAll}/>
                                </TableCell>
                                <TableCell>Klubb</TableCell>
                                <TableCell>Fakturanummer</TableCell>
                                <TableCell>Status i Eventor</TableCell>
                                <TableCell align="right">Belopp</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {invoices.map(invoice =>
                                <Row key={refOf(invoice)} invoice={invoice}
                                    checked={selected.has(refOf(invoice))}
                                    onToggle={() => toggle(refOf(invoice))}
                                    disabled={busy || loading}
                                    result={results[refOf(invoice)]}/>)}
                        </TableBody>
                    </Table>
                </TableContainer>
            }
        </div>
    )
})


export default EventorInvoiceImport
